import type {
  UseQueryResult,
  UseMutationResult,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';
import { useQuery, useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAxios } from './useAxios';
import type { Endpoint } from './apiClient.types';

/**
 * GET request with automatic caching via React Query
 *
 * @template T - Response data type
 * @template R - Data transformation type (optional)
 *
 * @param endpoint - Object with { url, queryKey }
 * @param config - Optional useQuery configuration
 * @returns UseQueryResult with data, isLoading, error, refetch
 */
export const useGetData = <T, R = T>(
  endpoint: Endpoint,
  config?: Omit<UseQueryOptions<T, AxiosError, R>, 'queryKey' | 'queryFn'>,
): UseQueryResult<R, AxiosError> => {
  const axiosInstance = useAxios();

  return useQuery<T, AxiosError, R>({
    queryKey: [endpoint.queryKey ?? 'getData', endpoint.url],
    queryFn: async () => {
      const { data } = await axiosInstance.get<T>(endpoint.url);
      return data;
    },
    ...config,
  });
};

/**
 * POST request for creating new resources
 *
 * @template T - Response data type
 * @template P - Payload type
 *
 * @param url - Endpoint URL
 * @param mutationOptions - Optional useMutation configuration
 * @returns UseMutationResult with mutate, isPending, error
 */
export const usePostData = <T, P = void>(
  url: string,
  mutationOptions?: Omit<UseMutationOptions<T, AxiosError, P>, 'mutationFn'>,
): UseMutationResult<T, AxiosError, P> => {
  const axiosInstance = useAxios();

  return useMutation<T, AxiosError, P>({
    mutationFn: async (payload: P) => {
      const { data } = await axiosInstance.post<T>(url, payload);
      return data;
    },
    ...mutationOptions,
  });
};

/**
 * PUT request for updating resources with response (200/201)
 *
 * @template T - Response data type
 * @template P - Payload type
 *
 * @param url - Endpoint URL
 * @param mutationOptions - Optional useMutation configuration
 * @returns UseMutationResult with mutate, isPending, error
 */
export const usePutData = <T, P = void>(
  url: string,
  mutationOptions?: Omit<UseMutationOptions<T, AxiosError, P>, 'mutationFn'>,
): UseMutationResult<T, AxiosError, P> => {
  const axiosInstance = useAxios();

  return useMutation<T, AxiosError, P>({
    mutationFn: async (payload: P) => {
      const { data } = await axiosInstance.put<T>(url, payload);
      return data;
    },
    ...mutationOptions,
  });
};

/**
 * PATCH request for partial updates with response (200)
 *
 * @template T - Response data type
 * @template P - Payload type
 *
 * @param url - Endpoint URL
 * @param mutationOptions - Optional useMutation configuration
 * @returns UseMutationResult with mutate, isPending, error
 */
export const usePatchData = <T, P = void>(
  url: string,
  mutationOptions?: Omit<UseMutationOptions<T, AxiosError, P>, 'mutationFn'>,
): UseMutationResult<T, AxiosError, P> => {
  const axiosInstance = useAxios();

  return useMutation<T, AxiosError, P>({
    mutationFn: async (payload: P) => {
      const { data } = await axiosInstance.patch<T>(url, payload);
      return data;
    },
    ...mutationOptions,
  });
};

/**
 * PUT request without response (204 No Content)
 *
 * @template P - Payload type
 *
 * @param url - Endpoint URL
 * @param mutationOptions - Optional useMutation configuration
 * @returns UseMutationResult with void return
 */
export const usePutDataNoContent = <P = void>(
  url: string,
  mutationOptions?: Omit<UseMutationOptions<void, AxiosError, P>, 'mutationFn'>,
): UseMutationResult<void, AxiosError, P> => {
  const axiosInstance = useAxios();

  return useMutation<void, AxiosError, P>({
    mutationFn: async (payload: P) => {
      await axiosInstance.put(url, payload);
    },
    ...mutationOptions,
  });
};

/**
 * DELETE request for removing resources
 *
 * @template P - Payload type (usually void)
 *
 * @param url - Endpoint URL
 * @param mutationOptions - Optional useMutation configuration
 * @returns UseMutationResult with void return
 */
export const useDeleteData = <P = void>(
  url: string,
  mutationOptions?: Omit<UseMutationOptions<void, AxiosError, P>, 'mutationFn'>,
): UseMutationResult<void, AxiosError, P> => {
  const axiosInstance = useAxios();

  return useMutation<void, AxiosError, P>({
    mutationFn: async (_payload?: P) => {
      await axiosInstance.delete(url);
    },
    ...mutationOptions,
  });
};
